<template>
    <div class="bg-white overflow-hidden shadow rounded-lg divide-y divide-gray-200">
        <div class="-ml-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap px-4 py-5 sm:p-6">
            <div class="ml-4 mt-2">
                <h3 class="text-lg leading-6 font-medium text-gray-900">Valuation</h3>
            </div>
        </div>

        <div class="px-4 py-5 sm:p-6">

            <Fieldset heading="Tax Settings">
                <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                    <div class="col-span-1 sm:col-span-2">
                        <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                            <div class="col-span-1 sm:col-span-1">
                                <FormField :field="state.proxy_tax.fields.tax_status" namespace="proxy_tax" :name="'tax_status'" class="mb-0" />
                            </div>
                        </div>
                    </div>
                </div>
            </Fieldset>

            <Fieldset heading="Reserve" subheading="Enter the NET values without tax and we'll calculate any tax for you">
                <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">

                    <div class="col-span-1 sm:col-span-2">
                        <div class="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                            <div class="col-span-1 sm:col-span-1">
                                <FormField :field="state.reserve_form.fields.master_reserve_status" namespace="reserve" :name="'master_reserve_status'" class="mb-4" />
                            </div>
                        </div>
                    </div>

                    <div v-if="state.reserve_form.fields.master_reserve.active" class="col-span-1 sm:col-span-1">
                        <FormField :field="state.reserve_form.fields.master_reserve" namespace="reserve" :name="'master_reserve'" class="mb-4" />
                        <Message
                            :active="state.reserve_warning.active"
                            type="info"
                            :heading="state.reserve_warning.heading"
                            :body="state.reserve_warning.body"
                            class="mb-2"
                        />
                    </div>

                    <div v-if="state.reserve_form.fields.master_reserve.active" class="col-span-1 sm:col-span-1">
                        <FormDisplayData :active="true" label="Gross Reserve (inc. VAT)" :value="taxable_master_reserve || 'No VAT'" />
                    </div>
                </div>
            </Fieldset>

        </div>

        <div class="px-4 py-4 sm:px-6 text-right">
            <Button type="button" width="w-auto" color="blue-outline" :onClick="ASSET_STORE.activatePrevStep">Back</Button>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => submitStep()" :disabled="!next_button_active">Next</Button>
        </div>

    </div>
</template>

<script>
import { useAssetStore } from '@sto/asset.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'

import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import {isValid, getPayload, getUserOrganisationType, penceToPounds, poundsToPence, monetise} from '@lib/helpers'
import { useRoute } from 'vue-router'
import config from '@/config'

import LoadingIcon from '@ico/LoadingIcon.vue'
import Fieldset from "@glo/Fieldset.vue";
export default {
    components: {
        Fieldset,
        LoadingIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ASSET_STORE = useAssetStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const asset_id = computed(() => {
            return ASSET_STORE?.asset?.id || null
        })

        const asset_data = computed(() => {
            return ASSET_STORE?.asset || null
        })

        const next_button_active = computed(() => {

            let active = true

            for (let f in state.reserve_form.fields) {
                if (state.reserve_form.fields[f].required && !state.reserve_form.fields[f].value) active = false
            }

            return active
        })

        const initialProxyTaxStatus = tax_status => {
            if (typeof tax_status === 'string' && tax_status.includes('apply_vat')) return 'apply_vat'
            if (typeof tax_status === 'string' && tax_status.includes('vat_qualifying')) return 'vat_qualifying'
            return 'no_vat'
        }

        const state = reactive({
            reserve_form: {
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.reserve_form.fields) {
                        if (state.reserve_form.fields[f].value != state.reserve_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    master_reserve_status: {
                        active: true,
                        label: 'Set a reserve?',
                        type: 'select',
                        value: 'use_reserve',
                        stored_value: '',
                        options: [
                            {
                                value: 'no_reserve',
                                label: 'No reserve'
                            },
                            {
                                value: 'tbc',
                                label: 'To be confirmed'
                            },
                            {
                                value: 'use_reserve',
                                label: 'Set reserve'
                            }
                        ],
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    master_reserve: {
                        active: asset_data.value?.master_reserve_status == 'use_reserve',
                        label: 'Reserve (exc. VAT)',
                        type: 'number',
                        value: '',
                        stored_value: '',
                        required: true,
                        pristine: true,
                        valid: false,
                        transformer: poundsToPence,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    tax_status: {
                        active: true,
                        type: 'hidden',
                        value: 'no_vat',
                        stored_value: '',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                }
            },
            proxy_tax: {
                fields: {
                    tax_status: {
                        active: true,
                        label: 'Tax status',
                        type: 'radio',
                        value: 'no_vat',
                        stored_value: '',
                        options: [
                            {
                                value: 'no_vat',
                                label: 'No VAT'
                            },
                            {
                                value: 'apply_vat',
                                label: 'Apply VAT'
                            }
                        ],
                        layout: 'inline',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    }
                }
            },
            reserve_warning: {
                active: false,
                heading: null,
                body: null
            },
            asset: {
                fields: {
                    master_opening_bid: {
                        active: true,
                        value: null,
                        stored_value: null,
                        type: 'hidden',
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                    bid_interval: {
                        active: true,
                        value: null,
                        stored_value: null,
                        type: 'hidden',
                        default_value: false,
                        required: false,
                        pristine: true,
                        valid: true,
                        transformer: poundsToPence,
                        validation: [],
                        message: '',
                        notes: '',
                        disabled: false
                    },
                }
            }
        })

        const setAssetData = asset => {
            if (!asset) return

            if (asset.master_reserve_status) {
                state.reserve_form.fields.master_reserve_status.value = asset.master_reserve_status
                state.reserve_form.fields.master_reserve_status.stored_value = asset.master_reserve_status

                state.reserve_form.fields.master_reserve.active = asset.master_reserve_status === 'use_reserve'
                state.reserve_form.fields.master_reserve.required = asset.master_reserve_status === 'use_reserve'
            }

            if (asset.master_reserve) {
                state.reserve_form.fields.master_reserve.value = penceToPounds(asset.master_reserve)
                state.reserve_form.fields.master_reserve.stored_value = penceToPounds(asset.master_reserve)
            }

            if (asset.tax_status) {
                state.reserve_form.fields.tax_status.value = asset.tax_status

                state.proxy_tax.fields.tax_status.value = initialProxyTaxStatus(asset.tax_status)
                state.proxy_tax.fields.tax_status.stored_value = initialProxyTaxStatus(asset.tax_status)
            }

            if (asset.master_opening_bid) {
                state.asset.fields.master_opening_bid.value = penceToPounds(asset.master_opening_bid)
            } else if (asset.master_reserve) {
                state.asset.fields.master_opening_bid.value = penceToPounds(asset.master_reserve)
            }

            if (asset.bid_interval) {
                state.asset.fields.bid_interval.value = penceToPounds(asset.bid_interval)
            } else if (asset.master_reserve) {
                state.asset.fields.bid_interval.value = ASSET_STORE.getSuggestedBidInterval(penceToPounds(asset.master_reserve))
            }

        }

        const submitStep = async () => {
            // fields will automatically create their own validation states
            if (state.reserve_form.loading || !isValid(state.reserve_form.fields)) return

            // if the value is present and unchaned, just skip to the next step
            if (!state.reserve_form.has_changes) return ASSET_STORE.setCreateModalState(true)

            state.reserve_form.loading = true

            try {

                const asset_payload = {
                    tax_status: state.reserve_form.fields.tax_status.value,
                    master_reserve_status: state.reserve_form.fields.master_reserve_status.value,
                    master_reserve: state.reserve_form.fields.master_reserve.value && !isNaN(state.reserve_form.fields.master_reserve.value)
                        ? poundsToPence(state.reserve_form.fields.master_reserve.value)
                        : null,
                    master_opening_bid: state.asset.fields.master_opening_bid.value && !isNaN(state.asset.fields.master_opening_bid.value)
                        ? poundsToPence(state.asset.fields.master_opening_bid.value)
                        : null,
                    bid_interval: state.asset.fields.bid_interval.value && !isNaN(state.asset.fields.bid_interval.value)
                        ? poundsToPence(state.asset.fields.bid_interval.value)
                        : null

                }
                await ASSET_STORE.update(ASSET_STORE.asset.id, asset_payload)
                ASSET_STORE.activateNextStep()

            } catch (error) {
                console.error('submitStep', error)
                SYSTEM_STORE.addError('Error', error?.message || 'There was a problem saving the information.')
            }

            state.reserve_form.loading = false
        }

        const taxable_master_reserve = computed(() => {
            if (!state.reserve_form.fields.master_reserve.value || !state.reserve_form.fields.master_reserve.active || !['apply_vat','vat_qualifying'].includes(state.reserve_form.fields.tax_status.value)) return null
            return monetise((1 + config.invoice.tax_rate) * poundsToPence(state.reserve_form.fields.master_reserve.value))
        })

        watch(
            () => asset_data.value,
            (new_value, old_value) => {
                setAssetData(asset_data.value)
            }
        )

        watch (
            () => state.proxy_tax.fields.tax_status.value,
            (new_value) => {
                // when the proxy tax status changes, update the actual tax status
                state.reserve_form.fields.tax_status.value = new_value
            }
        )

        watch(
            () => state.reserve_form.fields.master_reserve_status.value,
            (new_value, old_value) => {
                if (new_value == 'use_reserve') {
                    state.reserve_form.fields.master_reserve.active = true
                    state.reserve_form.fields.master_reserve.required = true
                } else {
                    state.reserve_form.fields.master_reserve.active = false
                    state.reserve_form.fields.master_reserve.required = false
                    state.reserve_form.fields.master_reserve.value = null
                }

            }
        )

        watch(
            () => state.reserve_form.fields.master_reserve.value,
            (new_value) => {

                if (!isNaN(new_value)) {
                    state.asset.fields.master_opening_bid.value = new_value
                    state.asset.fields.bid_interval.value = ASSET_STORE.getSuggestedBidInterval(new_value)
                }
            }
        )

        return {
            ASSET_STORE,
            state,
            next_button_active,
            taxable_master_reserve,
            submitStep
        }
    }
}
</script>

